import { useMemo, createContext, useContext, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { noop } from '@frontend/utils';

import type { AnnotationActionCallbackWithLoading } from 'modules/common/components/findings';
import type { ISuggestionAnnotation } from 'modules/common/viewer/pdfViewer/annotationCommands/SuggestionAnnotation';
import { SUGGESTION_ANNOTATION_TYPES } from 'modules/common/viewer/pdfViewer/annotationCommands/consts';
import {
  ANNOTATION_METADATA_TYPES,
  type FieldSuggestionMetadata,
  type FoundInFileMetadata,
} from 'modules/common/viewer/pdfViewer/annotationCommands/types';
import { usePartSetupSuggestionsInfo } from 'modules/viewerPage/apps/quotePartViewerApps/hooks/usePartSetupSuggestionsInfo';
import { usePartDescriptorSyncSideEffects } from 'modules/viewerPage/apps/quotePartViewerApps/partSetup/components/PartDescriptorForm';
import { usePartDescriptorsUpdateWithSideEffects } from 'modules/viewerPage/apps/quotePartViewerApps/partSetup/components/PartDescriptorWithSuggestions';
import { getActivePart } from 'modules/viewerPage/data/reducer';

export type PartDescriptorAnnotationActions = {
  setPartNumber?: AnnotationActionCallbackWithLoading;
  setDescription?: AnnotationActionCallbackWithLoading;
  setRevision?: AnnotationActionCallbackWithLoading;
};

type ProcessAnnotationResult = {
  partNumber?: string;
  revision?: string;
  description?: string;
};

type PartDescriptorActionData = {
  partDescriptorActions: (
    data: ProcessAnnotationResult,
  ) => PartDescriptorAnnotationActions;
  processAnnotation: (
    annot: ISuggestionAnnotation | null,
  ) => ProcessAnnotationResult | null;
};

export const DEFAULT_PART_DESCRIPTOR_ACTIONS = () => ({
  setPartNumber: {
    fn: noop,
    isLoading: false,
  },
  setDescription: {
    fn: noop,
    isLoading: false,
  },
  setRevision: {
    fn: noop,
    isLoading: false,
  },
});

const DEFAULT_ACTION_DATA: PartDescriptorActionData = {
  partDescriptorActions: DEFAULT_PART_DESCRIPTOR_ACTIONS,
  processAnnotation: () => null,
};

const PartDescriptorActionDataContext =
  createContext<PartDescriptorActionData>(DEFAULT_ACTION_DATA);

type PartDescriptorActionDataProviderProps = {
  children: React.ReactNode;
  canEditPart: boolean;
};

export const PartDescriptorActionDataProvider = ({
  children,
  canEditPart,
}: PartDescriptorActionDataProviderProps) => {
  const { suggestionsList } = usePartSetupSuggestionsInfo();
  const suggestions = suggestionsList.data ?? [];

  const activePart = useSelector(getActivePart);
  const { partNumber, revision, description } = activePart ?? {};

  // ---- mutation setup hooks (no fetch on mount) ----
  const { additionalOnSave } = usePartDescriptorSyncSideEffects();
  const {
    onSave: onSavePartNumber,
    isUpdatingPartDescriptors: isUpdatingPartNumber,
  } = usePartDescriptorsUpdateWithSideEffects({
    partUuid: activePart?.uuid ?? '',
    name: 'partNumber',
    suggestions,
    initialValue: partNumber ?? null,
    additionalOnSave,
  });
  const {
    onSave: onSaveRevision,
    isUpdatingPartDescriptors: isUpdatingRevision,
  } = usePartDescriptorsUpdateWithSideEffects({
    partUuid: activePart?.uuid ?? '',
    name: 'revision',
    suggestions,
    initialValue: revision ?? null,
    additionalOnSave,
  });
  const {
    onSave: onSaveDescription,
    isUpdatingPartDescriptors: isUpdatingDescription,
  } = usePartDescriptorsUpdateWithSideEffects({
    partUuid: activePart?.uuid ?? '',
    name: 'description',
    suggestions,
    initialValue: description ?? null,
    additionalOnSave,
  });

  const processAnnotation = useCallback(
    (annot: ISuggestionAnnotation | null): ProcessAnnotationResult | null => {
      if (!annot || !canEditPart || !activePart) {
        return null;
      }

      const { rawAnnotation } = annot;

      // If it's a field suggestion, we need to check its metadata and return the appropriate values
      if (rawAnnotation.type === SUGGESTION_ANNOTATION_TYPES.FIELD_SUGGESTION) {
        // But if the metadata doesn't match for some reason, return empty
        if (
          !rawAnnotation.metadata ||
          rawAnnotation.metadata.type !==
            ANNOTATION_METADATA_TYPES.FIELD_SUGGESTION
        ) {
          return {};
        }

        // If the metadata matches, we can return the appropriate values
        const metadata = rawAnnotation.metadata as FieldSuggestionMetadata;
        const { fieldSuggestion } = metadata;

        if (fieldSuggestion.fieldName === 'part_number') {
          return { partNumber: fieldSuggestion.suggestion as string };
        } else if (fieldSuggestion.fieldName === 'revision') {
          return { revision: fieldSuggestion.suggestion as string };
        } else if (fieldSuggestion.fieldName === 'description') {
          return { description: fieldSuggestion.suggestion as string };
        }

        return {};

        // Similarly, if it's a found in files annotation, we need to check its metadata and return the appropriate values
      } else if (
        rawAnnotation.type === SUGGESTION_ANNOTATION_TYPES.FOUND_IN_FILES
      ) {
        // But if the metadata doesn't match, return empty
        if (
          !rawAnnotation.metadata ||
          rawAnnotation.metadata.type !==
            ANNOTATION_METADATA_TYPES.FOUND_IN_FILES
        ) {
          return {};
        }

        const { partBotResult } = rawAnnotation.metadata as FoundInFileMetadata;
        if (
          partBotResult.category === 'Part Number' ||
          partBotResult.category === 'Drawing Number'
        ) {
          return { partNumber: rawAnnotation.content };
        } else if (partBotResult.category === 'Revision') {
          return { revision: rawAnnotation.content };
        } else if (partBotResult.category === 'Description') {
          return { description: rawAnnotation.content };
        }

        return {};
      }

      return null;
    },
    [canEditPart, activePart],
  );

  const partDescriptorActions = useCallback(
    ({
      partNumber: partNumberFromAnnotation,
      revision: revisionFromAnnotation,
      description: descriptionFromAnnotation,
    }: {
      partNumber?: string;
      revision?: string;
      description?: string;
    }) => {
      if (partNumberFromAnnotation && partNumber !== partNumberFromAnnotation) {
        return {
          setPartNumber: {
            fn: () => onSavePartNumber(partNumberFromAnnotation),
            isLoading: isUpdatingPartNumber,
          },
        };
      }

      if (revisionFromAnnotation && revision !== revisionFromAnnotation) {
        return {
          setRevision: {
            fn: () => onSaveRevision(revisionFromAnnotation),
            isLoading: isUpdatingRevision,
          },
        };
      }

      if (
        descriptionFromAnnotation &&
        description !== descriptionFromAnnotation
      ) {
        return {
          setDescription: {
            fn: () => onSaveDescription(descriptionFromAnnotation),
            isLoading: isUpdatingDescription,
          },
        };
      }

      return {};
    },
    [
      partNumber,
      revision,
      description,
      onSavePartNumber,
      onSaveRevision,
      onSaveDescription,
      isUpdatingPartNumber,
      isUpdatingRevision,
      isUpdatingDescription,
    ],
  );

  const value = useMemo(
    () => ({ partDescriptorActions, processAnnotation }),
    [partDescriptorActions, processAnnotation],
  );

  return (
    <PartDescriptorActionDataContext.Provider value={value}>
      {children}
    </PartDescriptorActionDataContext.Provider>
  );
};

export const usePartDescriptorActionData = (): PartDescriptorActionData =>
  useContext(PartDescriptorActionDataContext);
